import type { JourneyDayResponse, JourneyResponse } from '@manasil/shared';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CollectionEditor } from '../components/CollectionEditor.js';
import { CoverCard } from '../components/CoverCard.js';
import { EditDayModal } from '../components/EditDayModal.js';
import { HeroCard } from '../components/HeroCard.js';
import { Shell } from '../components/Shell.js';
import {
  Empty,
  Field,
  FieldGroup,
  Loading,
  Modal,
  StatusPill,
  errorMessage,
  formatDuration,
  useToast,
} from '../components/ui.js';
import { resolveMediaUrl } from '../lib/api.js';
import { usePlayer } from '../lib/player.js';
import {
  useArchiveJourney,
  useCreateDay,
  useDays,
  useDeleteDay,
  useGoals,
  useJourney,
  usePublishCheck,
  useSetPublished,
  useUpdateJourney,
} from '../lib/queries.js';

export function JourneyEditorPage() {
  const { id = '' } = useParams();
  const { data: journey, isLoading } = useJourney(id);
  const collection = journey?.kind === 'COLLECTION';
  // A collection has sections, not days; the hook is skipped rather than asked for nothing.
  const { data: days } = useDays(id, journey !== undefined && !collection);
  const [adding, setAdding] = useState(false);

  if (isLoading || !journey) {
    return (
      <Shell title="Journey">
        <Loading />
      </Shell>
    );
  }

  const dayCount = days?.length ?? 0;
  const durationDays = journey.durationDays ?? 0;
  const atCapacity = dayCount >= durationDays;

  return (
    <Shell
      title={journey.title}
      subtitle={`${collection ? 'Collection' : `${durationDays} days`} · ${journey.dailyMinutes} min a day · ${journey.isPremium ? 'Premium' : 'Free'}`}
      actions={
        <div className="row" style={{ gap: 9 }}>
          <StatusPill published={journey.published} />
          <Link to="/journeys" className="btn btn-ghost btn-sm">
            All journeys
          </Link>
        </div>
      }
    >
      <div className="editor">
        <div className="stack" style={{ gap: 20 }}>
          {/* Side by side: the two are chosen together and want opposite compositions,
              so seeing one without the other is how you end up with the same picture in
              both slots. */}
          <div className="image-row">
            <CoverCard journey={journey} />
            <HeroCard
              journey={journey}
              // Lowest day number, not days[0] — a reorder can leave the array unsorted.
              dayOneBanner={
                [...(days ?? [])].sort((a, b) => a.dayNumber - b.dayNumber)[0]?.activity.story
                  ?.bannerUrl ?? null
              }
            />
          </div>

          {collection ? (
            <CollectionEditor journeyId={id} />
          ) : (
            <div className="card">
              <div className="card-head">
                <div>
                  <h3>Days</h3>
                  <p className="hint">
                    {dayCount} of {durationDays} built. Day order is the order a user earns them.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setAdding(true)}
                  disabled={atCapacity}
                  title={atCapacity ? `This is a ${durationDays}-day journey` : undefined}
                >
                  Add day
                </button>
              </div>

              {dayCount === 0 ? (
                <Empty
                  mark="◷"
                  title="No days yet"
                  hint="Adding a day creates its story slot at the same time."
                />
              ) : (
                <div className="day-grid">
                  {days?.map((day) => (
                    <DayRow key={day.id} journeyId={id} day={day} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="side">
          <PublishPanel
            journeyId={id}
            published={journey.published}
            title={journey.title}
            collection={collection}
          />
          <ArtworkPanel journey={journey} />
          <MetadataPanel journeyId={id} />
        </div>
      </div>

      {adding ? <AddDayModal journeyId={id} onClose={() => setAdding(false)} /> : null}
    </Shell>
  );
}

/* Line icons rather than emoji: emoji render differently on every platform and cannot take
   currentColor, so they fight a dark UI. 24-box paths, sized where they are used. */
function PencilIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 7h16M9 7V4.5h6V7M6.5 7l.9 12.5h9.2L17.5 7" />
    </svg>
  );
}

/* ─────────────────────── a single day ─────────────────────── */

function DayRow({ journeyId, day }: { journeyId: string; day: JourneyDayResponse }) {
  const remove = useDeleteDay(journeyId);
  const player = usePlayer();
  const [editing, setEditing] = useState(false);

  const story = day.activity.story;
  const isCurrent = player.now?.storyId === story?.id;

  function listen() {
    if (!story?.hasAudio) return;
    void player.play({
      storyId: story.id,
      title: story.title,
      context: `Day ${day.dayNumber}`,
    });
  }

  return (
    <>
      <article className="day-tile">
        {/* The banner, big. It is what the player shows, what the phone's lock screen shows
            during playback, and for day 1 what the home shelf tile uses — so it is the thing
            worth seeing at a glance, and its absence worth noticing. */}
        <div className="day-art">
          {story?.bannerUrl ? (
            <img src={resolveMediaUrl(story.bannerUrl)} alt="" />
          ) : (
            <span className="day-art-none" title="No banner for this day">
              ▧
            </span>
          )}
          <span className="day-badge nums">Day {day.dayNumber}</span>
          {story?.hasAudio ? (
            <button
              type="button"
              className="day-play"
              onClick={listen}
              aria-pressed={isCurrent && player.playing}
              aria-label={`Play day ${day.dayNumber}: ${story.title}`}
              title="Listen"
            >
              {isCurrent && player.playing ? '❚❚' : '▶'}
            </button>
          ) : null}
        </div>

        <div className="day-body">
          <div className="day-title">{day.title}</div>
          <div className="day-meta">
            {story?.hasAudio ? (
              <span className="pill pill-ok">
                <span className="dot" />
                {formatDuration(story.durationSec)}
              </span>
            ) : (
              <span className="pill pill-missing">No audio</span>
            )}
            {/* Called out rather than merely absent: a day with no banner is silent art on
                the player and on the lock screen. */}
            {story && !story.bannerUrl ? (
              <span className="pill pill-missing">No banner</span>
            ) : null}
          </div>
          {story ? <div className="day-story dim truncate">{story.title}</div> : null}
        </div>

        {/* Icon-only, so the tile stays a tile. The label lives in aria-label and title
            rather than on screen — a bare icon is unusable to a screen reader otherwise. */}
        <div className="day-actions">
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => setEditing(true)}
            aria-label={`Edit day ${day.dayNumber}`}
            title="Edit"
          >
            <PencilIcon />
          </button>

          <button
            type="button"
            className="btn btn-icon btn-danger"
            onClick={() => {
              if (confirm(`Delete day ${day.dayNumber}? Its story and audio go with it.`)) {
                remove.mutate(day.id);
              }
            }}
            disabled={remove.isPending}
            aria-label={`Delete day ${day.dayNumber}`}
            title="Delete"
          >
            <TrashIcon />
          </button>
        </div>
      </article>

      {editing ? (
        <EditDayModal journeyId={journeyId} day={day} onClose={() => setEditing(false)} />
      ) : null}
    </>
  );
}

/* ─────────────────────── the images, as uploaded ─────────────────────── */

/**
 * The two journey-level images, plain.
 *
 * The cards on the left show what the APP makes of them — cropped to a shape, dimmed under
 * a scrim, with chrome over the top. Useful for judging composition, useless for answering
 * "is this even the right picture". This is the file itself, at whatever shape it really is.
 *
 * Dimensions come from what the browser measured at upload, so a cover that is not 2:1 or a
 * hero that is not 1.38:1 is visible here as a number rather than as a surprise in the app.
 */
function ArtworkPanel({ journey }: { journey: JourneyResponse }) {
  const images = [
    {
      label: 'Cover',
      url: journey.coverUrl,
      width: journey.coverWidth,
      height: journey.coverHeight,
      ideal: '1200 × 600',
    },
    {
      label: 'Home hero',
      url: journey.heroUrl,
      width: journey.heroWidth,
      height: journey.heroHeight,
      ideal: '1170 × 846',
    },
  ];

  return (
    <div className="card">
      <div className="card-head">
        <h3>The images</h3>
      </div>
      <div className="card-pad art-list">
        {images.map((image) => (
          <figure className="art-item" key={image.label}>
            <figcaption>
              <span>{image.label}</span>
              <span className="hint nums">
                {image.width ? `${image.width} × ${image.height}` : image.ideal}
              </span>
            </figcaption>
            {image.url ? (
              <img src={resolveMediaUrl(image.url)} alt={`${image.label} for ${journey.title}`} />
            ) : (
              <span className="art-empty">Not uploaded</span>
            )}
          </figure>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────── publish panel ─────────────────────── */

function PublishPanel({
  journeyId,
  published,
  title,
  collection,
}: { journeyId: string; published: boolean; title: string; collection: boolean }) {
  const { data: check, isLoading } = usePublishCheck(journeyId);
  const setPublished = useSetPublished(journeyId);
  const archive = useArchiveJourney(journeyId);
  const navigate = useNavigate();
  const { push } = useToast();

  async function remove() {
    const confirmed = confirm(
      `Remove "${title}"?\n\nIt leaves the catalogue and the portal. Nothing is deleted — any progress against it stays in history, and the title becomes free to reuse.`,
    );
    if (!confirmed) return;
    try {
      await archive.mutateAsync();
      push(`Removed "${title}"`);
      navigate('/journeys');
    } catch (caught) {
      push(errorMessage(caught), 'err');
    }
  }

  async function toggle() {
    try {
      await setPublished.mutateAsync(!published);
      push(published ? 'Unpublished' : 'Published — this is now live');
    } catch (caught) {
      push(errorMessage(caught), 'err');
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Publish</h3>
        <StatusPill published={published} />
      </div>
      <div className="card-pad stack" style={{ gap: 14 }}>
        {isLoading ? (
          <Loading />
        ) : check?.publishable ? (
          <div className="ready">
            <span aria-hidden="true">✓</span>
            <span>Everything a user needs to finish this is in place.</span>
          </div>
        ) : (
          <div className="issues">
            {check?.issues.map((issue, index) => (
              <div className="issue" key={`${issue.code}-${issue.dayNumber ?? index}`}>
                <span className="issue-mark" aria-hidden="true">
                  ●
                </span>
                <span>{issue.message}</span>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          className={published ? 'btn' : 'btn btn-primary'}
          onClick={toggle}
          disabled={setPublished.isPending || (!published && !check?.publishable)}
        >
          {setPublished.isPending ? 'Working…' : published ? 'Unpublish' : 'Publish journey'}
        </button>

        {!published && !check?.publishable ? (
          <p className="hint">
            {collection
              ? 'A collection goes live with whatever is recorded: it needs a section and at least one episode with audio. Episodes without audio stay hidden in the app until they get it.'
              : 'Publishing is blocked until every day has audio — a live journey where day 3 is silent is worse than one that is not live yet.'}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/* ─────────────────────── metadata panel ─────────────────────── */

/**
 * Rename the journey (asked for 2026-10-20). The server keeps titles unique per language
 * and re-derives the key used for that check, so a clash comes back as a conflict and is
 * shown rather than silently ignored. Saving is explicit: a title is read everywhere in
 * the app, so it should not change on every keystroke the way a toggle does.
 */
function TitleField({ journey }: { journey: JourneyResponse }) {
  const update = useUpdateJourney(journey.id);
  const { push } = useToast();
  const [title, setTitle] = useState(journey.title);
  const trimmed = title.trim();
  const changed = trimmed.length > 0 && trimmed !== journey.title;

  async function save() {
    if (!changed) return;
    try {
      await update.mutateAsync({ title: trimmed });
      push(`Renamed to "${trimmed}"`);
    } catch (caught) {
      push(errorMessage(caught), 'err');
    }
  }

  return (
    <Field label="Title" hint="What the app calls this journey everywhere.">
      <div className="row" style={{ gap: 8 }}>
        <input
          className="input"
          value={title}
          maxLength={140}
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void save();
            }
          }}
          disabled={update.isPending}
        />
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => void save()}
          disabled={!changed || update.isPending}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Field>
  );
}

function MetadataPanel({ journeyId }: { journeyId: string }) {
  const { data: journey } = useJourney(journeyId);
  const { data: goals } = useGoals();
  const update = useUpdateJourney(journeyId);
  const { push } = useToast();

  if (!journey) return null;

  /**
   * A journey belongs to exactly one category, so this picks rather than toggles —
   * choosing a new one replaces the old, and tapping the current one does nothing. There
   * is deliberately no way to end up with none.
   */
  async function pickCategory(key: string) {
    if (!journey || journey.goalKeys[0] === key) return;
    try {
      await update.mutateAsync({ goalKeys: [key] });
    } catch (caught) {
      push(errorMessage(caught), 'err');
    }
  }

  async function togglePremium() {
    if (!journey) return;
    try {
      await update.mutateAsync({ isPremium: !journey.isPremium });
    } catch (caught) {
      push(errorMessage(caught), 'err');
    }
  }

  /**
   * The two Home shelves. Curated, not per-user — these say what the app features, which is
   * why they live beside Access rather than anywhere near a user's own data.
   */
  async function toggleFavourite() {
    if (!journey) return;
    try {
      await update.mutateAsync({ isFavourite: !journey.isFavourite });
    } catch (caught) {
      push(errorMessage(caught), 'err');
    }
  }

  async function toggleTopChoice() {
    if (!journey) return;
    try {
      await update.mutateAsync({ isTopChoice: !journey.isTopChoice });
    } catch (caught) {
      push(errorMessage(caught), 'err');
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Details</h3>
      </div>
      <div className="card-pad stack" style={{ gap: 16 }}>
        {/* Keyed on the saved title so the draft resets after a rename, and after a
            refetch, without the field holding on to stale text. */}
        <TitleField key={journey.title} journey={journey} />

        <FieldGroup label="Category" hint="What this journey is matched on. Pick one.">
          <div className="chips">
            {goals
              ?.filter((goal) => goal.active || journey.goalKeys.includes(goal.key))
              .map((goal) => (
                <button
                  key={goal.key}
                  type="button"
                  className="chip"
                  aria-pressed={journey.goalKeys[0] === goal.key}
                  onClick={() => pickCategory(goal.key)}
                  disabled={update.isPending}
                >
                  {goal.name.en}
                </button>
              ))}
          </div>
        </FieldGroup>

        <div className="stack" style={{ gap: 4 }}>
          <span className="label">Access</span>
          <button
            type="button"
            className="chip"
            aria-pressed={journey.isPremium}
            onClick={togglePremium}
          >
            {journey.isPremium ? 'Premium' : 'Free'}
          </button>
        </div>

        <FieldGroup
          label="Home shelves"
          hint="Which shelf this appears on in the app's home screen. Unpublished journeys show under Coming Soon automatically."
        >
          <div className="chips">
            <button
              type="button"
              className="chip"
              aria-pressed={journey.isFavourite}
              onClick={toggleFavourite}
              disabled={update.isPending}
            >
              Your Favourites
            </button>
            <button
              type="button"
              className="chip"
              aria-pressed={journey.isTopChoice}
              onClick={toggleTopChoice}
              disabled={update.isPending}
            >
              Customers' Top Choice
            </button>
          </div>
        </FieldGroup>

        <div className="stack" style={{ gap: 4 }}>
          <span className="label">Description</span>
          <p className="muted" style={{ fontSize: 13 }}>
            {journey.description}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── add day modal ─────────────────────── */

function AddDayModal({ journeyId, onClose }: { journeyId: string; onClose: () => void }) {
  const create = useCreateDay(journeyId);
  const { push } = useToast();
  const [title, setTitle] = useState('');
  const [storyTitle, setStoryTitle] = useState('');
  const [storyDescription, setStoryDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      const day = await create.mutateAsync({
        title: title.trim(),
        ...(storyTitle.trim() ? { storyTitle: storyTitle.trim() } : {}),
        storyDescription: storyDescription.trim(),
      });
      push(`Day ${day.dayNumber} added`);
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <Modal
      title="Add day"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={create.isPending || title.trim().length === 0}
          >
            {create.isPending ? 'Adding…' : 'Add day'}
          </button>
        </>
      }
    >
      {error ? <div className="banner">{error}</div> : null}

      <Field
        label="Day title"
        hint="This is what the app shows as tomorrow's title on the Day Complete screen."
      >
        <input
          className="input"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Notice the spiral"
        />
      </Field>

      <Field label="Story title" hint="Leave blank to reuse the day title.">
        <input
          className="input"
          value={storyTitle}
          onChange={(event) => setStoryTitle(event.target.value)}
          placeholder="Quiet Your Mind"
        />
      </Field>

      <Field label="Story description">
        <textarea
          className="textarea"
          value={storyDescription}
          onChange={(event) => setStoryDescription(event.target.value)}
          placeholder="A gentle story to help you slow down your thoughts."
        />
      </Field>
    </Modal>
  );
}
